import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { switchMap } from 'rxjs';
import { PatientResponse } from '../models/patient.model';
import { PatientsService } from '../services/patients.service';
import { PatientModalComponent } from './patient-modal/patient-modal.component';

@Component({
  selector: 'app-patients',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    TableModule,
    ProgressSpinnerModule,
    PatientModalComponent
  ],
  templateUrl: './patients.component.html',
  styleUrls: ['./patients.component.scss']
})
export class PatientsComponent implements OnInit {
  // Make Math available in template
  Math = Math;
  
  searchText: string = '';
  
  // Use signals for reactive data
  patients = signal<PatientResponse[]>([]);
  recentPatients = signal<PatientResponse[]>([]);
  filteredPatients = signal<PatientResponse[]>([]);
  
  // Statistics variables
  totalPatients = signal<number>(0);
  newThisMonth = signal<string>('');
  appointmentsThisWeek = signal<string>('');
  averageVisitsPerPatient = signal<string>('');
  
  // Pagination variables
  first: number = 0;
  rows: number = 10;
  totalRecords = signal<number>(0);

  // Patient Modal
  displayPatientModal: boolean = false;
  modalMode: 'add' = 'add';
  selectedPatient: PatientResponse | null = null;
  
  // Loading and error states
  isLoading = signal<boolean>(false);
  error = signal<string | null>(null);

  constructor(
    private patientsService: PatientsService
  ) {}

  ngOnInit() {
    this.loadAllPatientsData();
  }

  onSearchInput(event: any) {
    this.searchText = event.target.value;
    this.onSearch();
  }

  onSearch() {
    this.patientsService.searchPatients(this.searchText).subscribe((patients) => {
      const normalized = patients.map((p) => this.normalizePatientRow(p));
      this.filteredPatients.set(normalized);
      this.totalRecords.set(normalized.length);
    });
  }

  loadAllPatientsData() {
    this.isLoading.set(true);
    this.error.set(null);
    
    // Load patients first, then statistics endpoint
    this.patientsService.getAllPatients().pipe(
      switchMap((patients) => {
        const normalized = patients.map((p) => this.normalizePatientRow(p));
        this.patients.set(normalized);
        this.filteredPatients.set([...normalized]);
        this.totalRecords.set(normalized.length);
        this.recentPatients.set(normalized.slice(0, 5));

        this.calculateBasicStatistics(normalized);
        
        // Now load statistics from backend API
        return this.patientsService.getPatientStatistics();
      })
    ).subscribe({
      next: (statistics) => {
        // Update statistics from backend
        this.totalPatients.set(statistics.totalPatients);
        this.newThisMonth.set(statistics.newThisMonth.toString());
        this.appointmentsThisWeek.set(statistics.appointmentsThisWeek.toString());
        this.averageVisitsPerPatient.set(statistics.averageVisitsPerPatient.toFixed(1));
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading data:', error);
        this.error.set('Failed to load data. Please try again.');
        this.isLoading.set(false);
        // Even if statistics fail, we still have patients loaded
      }
    });
  }

  private calculateBasicStatistics(patients: PatientResponse[]): void {
    // Initialize statistics (will be updated from backend API)
    // These are placeholders until the statistics API call completes
    this.totalPatients.set(patients.length);
    this.newThisMonth.set('...');
    this.appointmentsThisWeek.set('...');
    this.averageVisitsPerPatient.set('...');
  }

  onFilterClick() {
    // Toggle between filter states or implement filter dialog
    // TODO: Implement filter functionality
  }

  showAddPatientModal() {
    this.modalMode = 'add';
    this.selectedPatient = null;
    this.displayPatientModal = true;
  }



  onPatientSaved(patient: PatientResponse) {
    // Refresh the patients list after adding/editing a patient
    this.loadAllPatientsData();
    this.displayPatientModal = false;
  }

  onModalClosed() {
    this.displayPatientModal = false;
    this.selectedPatient = null;
  }

  /** Normalize API row (camelCase or snake_case; ISO string or Jackson [y,m,d] dates). */
  private normalizePatientRow(raw: PatientResponse): PatientResponse {
    const r = raw as unknown as Record<string, unknown>;
    const dob = this.coerceApiDateOnly(r['dateOfBirth'] ?? r['date_of_birth']);
    const lastVisit = this.coerceApiDateTime(r['lastVisit'] ?? r['last_visit']);
    return {
      ...raw,
      dateOfBirth: dob ?? '',
      lastVisit
    };
  }

  /** YYYY-MM-DD or undefined. */
  private coerceApiDateOnly(value: unknown): string | undefined {
    if (value == null || value === '') {
      return undefined;
    }
    if (Array.isArray(value) && value.length >= 3) {
      const y = Number(value[0]);
      const m = Number(value[1]);
      const d = Number(value[2]);
      if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
        return undefined;
      }
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
    if (typeof value === 'string') {
      const s = value.trim();
      if (!s) {
        return undefined;
      }
      return s.slice(0, 10);
    }
    return undefined;
  }

  /** Calendar date part from ISO datetime or Jackson array (first three entries = y,m,d). */
  private coerceApiDateTime(value: unknown): string | undefined {
    return this.coerceApiDateOnly(value);
  }

  /** US short date mm/dd/yyyy (compact for table layout). */
  formatDateMdYyyy(dateString: string | undefined | null): string {
    if (dateString === undefined || dateString === null) {
      return '—';
    }
    const trimmed = String(dateString).trim();
    if (!trimmed) {
      return '—';
    }

    const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed.slice(0, 10));
    if (ymd) {
      const y = parseInt(ymd[1], 10);
      const m = parseInt(ymd[2], 10) - 1;
      const d = parseInt(ymd[3], 10);
      const date = new Date(y, m, d);
      if (Number.isNaN(date.getTime())) {
        return '—';
      }
      const mm = (date.getMonth() + 1).toString().padStart(2, '0');
      const dd = date.getDate().toString().padStart(2, '0');
      const yyyy = date.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    }

    try {
      const date = new Date(trimmed);
      if (Number.isNaN(date.getTime())) {
        return '—';
      }
      const mm = (date.getMonth() + 1).toString().padStart(2, '0');
      const dd = date.getDate().toString().padStart(2, '0');
      const yyyy = date.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    } catch {
      return '—';
    }
  }

  getLastVisitDate(patient: PatientResponse): string {
    const lv = patient.lastVisit;
    if (lv !== undefined && lv !== null && String(lv).trim() !== '') {
      return this.formatDateMdYyyy(String(lv));
    }
    return 'No visits yet';
  }
} 